import clsx from "clsx";
import { type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from "react";

import type { OdbNode } from "../../api/types";
import { t } from "../../i18n/i18n";
import { useUi } from "../../state/ui";
import { bi, echelonInfo } from "./app6";
import type { UnitInfo } from "./editorModel";
import {
  addUnits,
  duplicateAt,
  fitEchelonAt,
  indentAt,
  moveAt,
  nodeAt,
  outdentAt,
  type Path,
  pathFromKey,
  pathKey,
  renumberAt,
  type TreeContext,
  updateAt,
  withoutCodes,
} from "./tree";
import { canDrop, DRAG_THRESHOLD, type DropPosition, dropPosition, moveNode } from "./treeDrag";
import { type LineAction, UnitLine } from "./UnitLine";

/** Arbre des unités de l'éditeur (motif `tree` d'ARIA) : sélection, dépliage, renommage dans la ligne, ajout,
 *  duplication, copier-coller de branche, glisser-déposer et clavier.
 *
 *  Clavier (arbre au point, hors saisie) : ↑ ↓ sélection ; → ← déplier, replier (ou aller au premier subordonné,
 *  à la formation) ; Entrée renomme ; F2 ouvre la palette ; Inser ou + ajoute un subordonné ; ⌘D duplique ; Suppr
 *  retire (sans confirmation : l'avis propose « Annuler ») ; Alt+↑ ↓ déplace parmi les sœurs ; Alt+→ fait de
 *  l'unité un subordonné de sa sœur précédente ; Alt+← la remonte d'un rang ; ⌘C / ⌘V copie et colle une branche. */

export interface TreeApply {
  (tree: OdbNode, options?: { select?: Path; group?: string; expand?: Path }): void;
}

interface DragState {
  from: string;
  pointerId: number;
  startX: number;
  startY: number;
  touch: boolean;
  active: boolean;
}

export function UnitTree({
  tree,
  ctx,
  infos,
  enemy,
  editable,
  selected,
  collapsed,
  canPaste,
  onSelect,
  onToggle,
  apply,
  onSeal,
  onRemove,
  onCopy,
  onPaste,
  onOpenPalette,
  catalogueDuplicate,
}: {
  tree: OdbNode;
  ctx: TreeContext;
  infos: Map<string, UnitInfo>;
  enemy: boolean;
  editable: boolean;
  selected: string;
  collapsed: Set<string>;
  canPaste: boolean;
  onSelect: (key: string) => void;
  onToggle: (key: string, open?: boolean) => void;
  apply: TreeApply;
  /** Fin d'une saisie de texte : la prochaine frappe ouvre une entrée d'Annuler. */
  onSeal: () => void;
  onRemove: (path: Path) => void;
  onCopy: (node: OdbNode) => void;
  onPaste: (path: Path) => void;
  onOpenPalette: (key: string) => void;
  catalogueDuplicate: (info: UnitInfo) => (() => void) | undefined;
}) {
  const notify = useUi((st) => st.notify);
  const [renaming, setRenaming] = useState<{ key: string; original: string } | null>(null);
  const [drag, setDrag] = useState<{ from: string; over: string | null; position: DropPosition; valid: boolean } | null>(null);
  const gesture = useRef<DragState | null>(null);
  // Cible courante du glisser, lue au relâcher (l'état React peut ne pas être encore rendu).
  const target = useRef<{ from: string; over: string | null; position: DropPosition; valid: boolean } | null>(null);
  const box = useRef<HTMLDivElement>(null);

  // Lignes visibles, dans l'ordre (les subordonnés d'une unité repliée sont cachés).
  const visible: UnitInfo[] = [];
  const visit = (node: OdbNode, path: Path) => {
    const info = infos.get(pathKey(path));
    if (info) visible.push(info);
    if (!collapsed.has(pathKey(path))) node.children.forEach((c, i) => visit(c, [...path, i]));
  };
  visit(tree, []);

  // L'arbre a le focus clavier (frappe, focus) ; perdu seulement quand le focus part ailleurs dans la page. Une ligne
  // retirée (Suppr), collée ou déplacée laisse le focus sur <body> : il revient alors sur la ligne sélectionnée, pour
  // que la touche suivante (Insert, flèches, ⌘D) agisse encore sur l'arbre.
  const keyboard = useRef(false);
  useEffect(() => {
    const row = box.current?.querySelector<HTMLElement>(`[data-unit-row="${selected}"]`);
    row?.scrollIntoView?.({ block: "nearest" });
    const active = document.activeElement;
    const inTree = box.current?.contains(active) && active?.tagName !== "INPUT";
    const lost = keyboard.current && (active === null || active === document.body);
    if (inTree || lost) row?.focus({ preventScroll: true });
  }, [selected, tree]);

  return (
    <div
      ref={box}
      role="tree"
      aria-label={t("Unités de l'ordre de bataille")}
      aria-multiselectable={false}
      className={clsx("@container min-w-0 select-none", drag && (drag.valid ? "cursor-grabbing" : "cursor-not-allowed"))}
      onKeyDown={(e) => {
        keyboard.current = true;
        onKey(e);
      }}
      onFocus={() => {
        keyboard.current = true;
      }}
      onBlur={(e) => {
        if (e.relatedTarget instanceof Node && !box.current?.contains(e.relatedTarget)) keyboard.current = false;
      }}
    >
      {visible.map((info) => (
        <UnitLine
          key={info.key}
          info={info}
          palette={ctx.palette}
          enemy={enemy}
          editable={editable}
          collapsed={collapsed.has(info.key)}
          selected={selected === info.key}
          renaming={renaming?.key === info.key}
          drop={drag && drag.over === info.key ? { position: drag.position, valid: drag.valid } : null}
          dragging={drag?.from === info.key}
          canPaste={canPaste}
          onSelect={() => onSelect(info.key)}
          onToggle={() => onToggle(info.key)}
          onRename={(name) => apply(updateAt(tree, info.path, (n) => ({ ...n, name })), { group: `name:${info.key}` })}
          onStartRename={() => startRename(info)}
          onRenameEnd={(cancel) => endRename(cancel)}
          onAdd={() => addHere(info.path, 1)}
          onDuplicate={() => duplicate(info.path)}
          onAction={(action) => lineAction(info, action)}
          onCatalogueDuplicate={catalogueDuplicate(info)}
          onHandlePointerDown={(e) => handleDown(e, info.key)}
        />
      ))}
    </div>
  );

  // --- Opérations ---------------------------------------------------------------------------------------------

  function addHere(path: Path, count: number) {
    apply(addUnits(tree, path, ctx, count), { expand: path, select: [...path, nodeAt(tree, path).children.length] });
  }

  function duplicate(path: Path) {
    if (path.length === 0) return;
    const result = duplicateAt(tree, path, ctx);
    apply(result.tree, { select: result.path });
  }

  /** Unité passée sous une autre formation : échelon ramené sous le sien si besoin (R1), avec un avis. */
  function settle(result: { tree: OdbNode; path: Path }) {
    const fitted = fitEchelonAt(result.tree, result.path, ctx);
    apply(fitted.tree, { select: result.path, expand: result.path.slice(0, -1) });
    if (fitted.lowered) {
      const parent = nodeAt(fitted.tree, result.path.slice(0, -1));
      notify({
        text: t("Échelon ramené à « {echelon} »", { echelon: bi(echelonInfo(ctx.palette, fitted.lowered)) }),
        detail: t("Sous « {parent} », une unité a un échelon inférieur au sien.", { parent: parent.name || t("Sans nom") }),
        tone: "info",
      });
    }
  }

  function startRename(info: UnitInfo) {
    if (!editable) return;
    onSelect(info.key);
    setRenaming({ key: info.key, original: info.node.name });
  }

  function endRename(cancel: boolean) {
    if (!renaming) return;
    const path = pathFromKey(renaming.key);
    if (cancel && nodeAt(tree, path).name !== renaming.original)
      apply(updateAt(tree, path, (n) => ({ ...n, name: renaming.original })), { group: `name:${renaming.key}` });
    setRenaming(null);
    onSeal();
    box.current?.querySelector<HTMLElement>(`[data-unit-row="${renaming.key}"]`)?.focus({ preventScroll: true });
  }

  function lineAction(info: UnitInfo, action: LineAction) {
    if (action === "add2" || action === "add3" || action === "add4") addHere(info.path, Number(action.slice(3)));
    else if (action === "copy") onCopy(withoutCodes(info.node));
    else if (action === "paste") onPaste(info.path);
    else if (action === "renumber") apply(renumberAt(tree, info.path));
    else if (action === "remove") onRemove(info.path);
  }

  // --- Clavier ------------------------------------------------------------------------------------------------

  function onKey(e: ReactKeyboardEvent<HTMLDivElement>) {
    if ((e.target as HTMLElement).tagName === "INPUT" || renaming) return;
    const index = visible.findIndex((v) => v.key === selected);
    const current = visible[index];
    if (!current) return;
    const path = current.path;
    const command = e.metaKey || e.ctrlKey;
    const consume = () => {
      e.preventDefault();
      e.stopPropagation();
    };
    if (e.altKey && editable && path.length > 0) {
      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        consume();
        const delta = e.key === "ArrowUp" ? -1 : 1;
        const last = path[path.length - 1] as number;
        const siblings = nodeAt(tree, path.slice(0, -1)).children.length;
        if (last + delta < 0 || last + delta >= siblings) return;
        apply(moveAt(tree, path, delta), { select: [...path.slice(0, -1), last + delta] });
        return;
      }
      if (e.key === "ArrowRight") {
        consume();
        const result = indentAt(tree, path);
        if (result) settle(result);
        return;
      }
      if (e.key === "ArrowLeft") {
        consume();
        const result = outdentAt(tree, path);
        if (result) apply(result.tree, { select: result.path });
        return;
      }
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      consume();
      const next = visible[Math.max(0, Math.min(visible.length - 1, index + (e.key === "ArrowDown" ? 1 : -1)))];
      if (next) onSelect(next.key);
    } else if (e.key === "ArrowRight") {
      consume();
      if (!current.node.children.length) return;
      if (collapsed.has(current.key)) onToggle(current.key, true);
      else onSelect(pathKey([...path, 0]));
    } else if (e.key === "ArrowLeft") {
      consume();
      if (current.node.children.length && !collapsed.has(current.key)) onToggle(current.key, false);
      else if (path.length) onSelect(pathKey(path.slice(0, -1)));
    } else if (e.key === "Home" || e.key === "End") {
      consume();
      const target = e.key === "Home" ? visible[0] : visible[visible.length - 1];
      if (target) onSelect(target.key);
    } else if (!editable) {
      return;
    } else if (e.key === "Enter") {
      consume();
      startRename(current);
    } else if (e.key === "F2") {
      consume();
      onOpenPalette(current.key);
    } else if ((e.key === "Insert" || e.key === "+") && current.childEchelon) {
      consume();
      addHere(path, 1);
    } else if (command && e.key.toLowerCase() === "d") {
      consume();
      duplicate(path);
    } else if (command && e.key.toLowerCase() === "c" && !window.getSelection()?.toString()) {
      consume();
      onCopy(withoutCodes(current.node));
    } else if (command && e.key.toLowerCase() === "v" && current.childEchelon) {
      consume();
      onPaste(path);
    } else if ((e.key === "Delete" || e.key === "Backspace") && path.length > 0) {
      consume();
      onRemove(path);
    }
  }

  // --- Glisser-déposer ----------------------------------------------------------------------------------------

  function handleDown(e: ReactPointerEvent<HTMLElement>, key: string) {
    if (e.button !== 0 || !editable) return;
    e.stopPropagation();
    const handle = e.currentTarget;
    gesture.current = { from: key, pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, touch: e.pointerType !== "mouse", active: false };
    handle.setPointerCapture?.(e.pointerId);
    const move = (event: PointerEvent) => {
      const g = gesture.current;
      if (!g || event.pointerId !== g.pointerId) return;
      if (!g.active) {
        const distance = Math.hypot(event.clientX - g.startX, event.clientY - g.startY);
        if (distance < (g.touch ? DRAG_THRESHOLD.touch : DRAG_THRESHOLD.mouse)) return;
        g.active = true; // pas de sélection au départ du geste : sur tablette, elle ouvrirait la fiche sur l'arbre
      }
      event.preventDefault();
      autoScroll(event.clientY);
      const row = (document.elementFromPoint(event.clientX, event.clientY) as HTMLElement | null)?.closest<HTMLElement>("[data-unit-row]");
      const over = row?.dataset.unitRow ?? null;
      if (!row || !over) {
        show({ from: g.from, over: null, position: "after", valid: false });
        return;
      }
      const rect = row.getBoundingClientRect();
      const info = infos.get(over);
      const inside = Boolean(info?.childEchelon);
      const position = dropPosition(event.clientY - rect.top, rect.height, inside);
      const valid = canDrop(pathFromKey(g.from), pathFromKey(over), position, inside);
      show({ from: g.from, over, position, valid });
    };
    const end = (event: PointerEvent) => {
      const g = gesture.current;
      if (!g || event.pointerId !== g.pointerId) return;
      handle.removeEventListener("pointermove", move);
      handle.removeEventListener("pointerup", end);
      handle.removeEventListener("pointercancel", end);
      handle.releasePointerCapture?.(event.pointerId);
      gesture.current = null;
      const state = target.current;
      target.current = null;
      setDrag(null);
      if (state && event.type === "pointerup" && state.valid && state.over) {
        const result = moveNode(tree, pathFromKey(state.from), pathFromKey(state.over), state.position);
        if (result) settle(result);
      }
    };
    // Échap pendant le glisser : le geste est abandonné, rien ne bouge.
    const cancel = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || !gesture.current?.active) return;
      event.preventDefault();
      event.stopPropagation();
      handle.dispatchEvent(new PointerEvent("pointercancel", { pointerId: gesture.current.pointerId }));
    };
    const cleanup = () => window.removeEventListener("keydown", cancel, true);
    handle.addEventListener("pointermove", move);
    handle.addEventListener("pointerup", end);
    handle.addEventListener("pointercancel", end);
    handle.addEventListener("pointerup", cleanup, { once: true });
    handle.addEventListener("pointercancel", cleanup, { once: true });
    window.addEventListener("keydown", cancel, true);
  }

  function show(state: NonNullable<typeof drag>) {
    target.current = state;
    setDrag(state);
  }

  function autoScroll(y: number) {
    let scroller: HTMLElement | null = box.current?.parentElement ?? null;
    while (scroller && getComputedStyle(scroller).overflowY !== "auto") scroller = scroller.parentElement;
    if (!scroller) return;
    const rect = scroller.getBoundingClientRect();
    if (y < rect.top + 32) scroller.scrollTop -= 12;
    else if (y > rect.bottom - 32) scroller.scrollTop += 12;
  }
}
